import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  VStack,
  Text,
  Heading,
  SimpleGrid,
  Button,
  Icon,
  Flex,
} from "@chakra-ui/react";
import { FaArrowRight } from "react-icons/fa";
import BlogCard from "./BlogCard";
import ScrollAnimationWrapper from "./ScrollAnimationWrapper";

// Dynamically import all blog posts
function importAllPosts() {
  const postContext = require.context('../pages/blogs/posts', false, /\.js$/);
  const posts = [];
  
  postContext.keys().forEach(key => {
    const post = postContext(key).post;
    if (post) {
      posts.push(post);
    }
  });
  
  // Sort posts by date (newest first) and get the 3 most recent
  return posts.sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateB - dateA;
  }).slice(0, 3);
}

const BlogSection = () => {
  const navigate = useNavigate();
  const recentPosts = importAllPosts();

  const handleViewAllClick = () => {
    navigate('/blog');
  };

  return (
    <Box
      as="section"
      id="blog-section"
      bg="brand.primary"
      py={{ base: "4rem", md: "5rem" }}
      position="relative"
      overflow="hidden"
    >
      {/* Background decorative elements */}
      <Box
        position="absolute"
        top="20%"
        left="-5%"
        w="250px"
        h="250px"
        borderRadius="full"
        bg="rgba(147, 51, 234, 0.05)"
        filter="blur(100px)"
        zIndex={0}
      />
      <Box
        position="absolute"
        bottom="15%"
        right="-5%"
        w="300px"
        h="300px"
        borderRadius="full"
        bg="rgba(59, 130, 246, 0.07)"
        filter="blur(120px)"
        zIndex={0}
      />

      <Container maxW="1200px" position="relative" zIndex={1}>
        <VStack spacing={{ base: 8, md: 12 }} align="stretch">
          {/* Section header */}
          <ScrollAnimationWrapper>
            <VStack spacing={3} align="center" textAlign="center">
              <Text
                color="brand.accent"
                fontWeight="600"
                letterSpacing="1px"
                textTransform="uppercase"
                fontSize="sm"
              >
                Latest Articles
              </Text>
              
              <Heading
                as="h2"
                fontSize={{ base: "3xl", md: "4xl" }}
                fontWeight="bold"
                textAlign="center"
                lineHeight="1.2"
              >
                Recent Blog Posts
              </Heading>
              
              <Text
                color="brand.text.secondary"
                maxW="600px"
                fontSize={{ base: "md", md: "lg" }}
              >
                Personal insights on development, growth, and technology
              </Text>
            </VStack>
          </ScrollAnimationWrapper>

          {/* Blog posts grid */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8} w="100%">
            {recentPosts.map((post, index) => (
              <ScrollAnimationWrapper
                key={post.id}
                delay={0.1 * index}
                direction="up"
              >
                <BlogCard
                  {...post}
                  index={index}
                  useAnimation={false} // Disable individual card animations since we're using ScrollAnimationWrapper
                />
              </ScrollAnimationWrapper>
            ))}
          </SimpleGrid>

          {/* View all blog posts button */}
          <ScrollAnimationWrapper delay={0.4} direction="up">
            <Flex
              justify="center"
              mt={{ base: 6, md: 10 }}
            >
              <Button
                onClick={handleViewAllClick}
                rightIcon={<Icon as={FaArrowRight} />}
                colorScheme="blue"
                size="lg"
                px={8}
                fontSize="md"
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "lg",
                }}
              >
                View All Posts
              </Button>
            </Flex>
          </ScrollAnimationWrapper>
        </VStack>
      </Container>
    </Box>
  );
};

export default BlogSection; 